// Subscriber detail — prototype-faithful (chitos-prototype screens2.jsx
// SubscriberDetail). Real data via orpc.subscribers.detail (which aggregates
// the subscriber's chits, dues, and recent payments). Fields the backend
// doesn't supply (on-time rate, kuladeivam) are omitted, not faked.

import type { ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import { current_language } from '../i18n/index.js';
import { format_inr } from '../lib/format.js';
import { orpc } from '../lib/orpc.js';
import { friendly_error } from '../lib/friendly-error.js';
import { NotifyMenu } from '../components/NotifyMenu.js';

interface SubscriberRec {
  id: string;
  name_ta: string;
  name_en: string;
  phone: string;
  email: string | null;
  aadhaar_last4: string | null;
  town_ta: string | null;
  town_en: string | null;
  occupation: string | null;
  risk_classification: string;
  status: string;
  created_at: string | Date;
}
interface ChitLite {
  id: string;
  name_ta: string;
  name_en: string;
}
interface DetailChit {
  chit: ChitLite;
  slot: number;
  total_paid_paise: number;
  total_expected_paise: number;
  upcoming_payout_month: string | null;
}
interface Payment {
  payment_id: string;
  chit_id: string;
  amount_paise: number;
  method: string;
  reference: string | null;
  timestamp: string | Date;
  due_month: string;
  chit_name_ta: string;
  chit_name_en: string;
}

function initials(name: string | null | undefined): string {
  if (!name) return '··';
  const p = name.trim().split(/\s+/).filter(Boolean);
  if (p.length === 0) return '··';
  if (p.length === 1) return p[0]!.slice(0, 2).toUpperCase();
  return (p[0]!.charAt(0) + p[p.length - 1]!.charAt(0)).toUpperCase();
}

export function SubscriberDetailPage(): ReactElement {
  const lang = current_language();
  const nav = useNavigate();
  const params = useParams<{ subscriber_id: string }>();
  const subscriber_id = params.subscriber_id ?? '';
  const t = (ta: string, en: string): string => (lang === 'ta' ? ta : en);

  const q = useQuery({
    queryKey: ['subscriber-detail', subscriber_id],
    queryFn: () => orpc.subscribers.detail({ subscriber_id }),
    enabled: !!subscriber_id,
  });

  if (q.isLoading) return <div className="page full"><div style={{ color: 'var(--ink-3)' }}>Loading…</div></div>;
  if (q.error) return <div className="page full"><div style={{ color: 'var(--bad)' }}>{friendly_error(q.error)}</div></div>;
  if (!q.data) return <div className="page full" />;

  const sub = q.data.subscriber as SubscriberRec;
  const chits = (q.data.chits ?? []) as DetailChit[];
  const payments = (q.data.recent_payments ?? []) as Payment[];
  const total_dues = q.data.total_dues_paise;
  const total_contributed = chits.reduce((s, c) => s + c.total_paid_paise, 0);
  const since = new Date(sub.created_at).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });

  return (
    <div className="page full">
      <div className="page-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button onClick={() => nav('/subscribers')} style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', color: 'var(--ink-3)', fontSize: 12, font: 'inherit' }}>
          ← {t('உறுப்பினர்கள்', 'Members')}
        </button>
        <NotifyMenu subscriber_id={sub.id} intent="custom" variant="inline" />
      </div>

      <div className="profile-head" style={{ marginBottom: 14 }}>
        <div className="profile-ava">{initials(sub.name_en)}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className={'h-title ' + (lang === 'ta' ? 'tamil' : '')} style={{ fontSize: 18 }}>
            {lang === 'ta' ? sub.name_ta : sub.name_en}
          </div>
          <div className="h-sub mono">
            {sub.phone}
            {sub.aadhaar_last4 ? ` · Aadhaar ••••${sub.aadhaar_last4}` : ''}
            {` · ${t('உறுப்பினர் முதல்', 'since')} ${since}`}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span className={'risk ' + sub.risk_classification}>{sub.risk_classification} risk</span>
            {sub.aadhaar_last4 && <span className="compliance-badge registered">KYC ✓</span>}
            {(sub.town_en || sub.town_ta) && (
              <span style={{ fontSize: 11, color: 'var(--ink-3)' }} className={lang === 'ta' ? 'tamil' : ''}>
                {lang === 'ta' ? sub.town_ta : sub.town_en}
              </span>
            )}
            {sub.occupation && (
              <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>· {sub.occupation}</span>
            )}
          </div>
        </div>
      </div>

      <div className="kpis" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div className="kpi">
          <div className="kpi-label">{t('செயலிலுள்ள சீட்டுகள்', 'Active chits')}</div>
          <div className="kpi-value">{chits.length}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">{t('மொத்தம் செலுத்தியது', 'Total contributed')}</div>
          <div className="kpi-value">{format_inr(total_contributed)}</div>
          <div className="kpi-sub">{t('across', 'across')} {chits.length} {t('சீட்டுகள்', 'chits')}</div>
        </div>
        <div className={'kpi' + (total_dues > 0 ? ' warn' : '')}>
          <div className="kpi-label">{t('தற்போதைய நிலுவை', 'Outstanding')}</div>
          <div className="kpi-value">{format_inr(total_dues)}</div>
        </div>
      </div>

      {/* Chits */}
      <div className="section-band">{t('சீட்டுகள்', 'Chits')}</div>
      <div className="card" style={{ padding: 0 }}>
        <table className="filings-table">
          <thead>
            <tr>
              <th>{t('சீட்டு', 'Chit')}</th>
              <th style={{ textAlign: 'right' }}>{t('நிலை', 'Slot')}</th>
              <th style={{ textAlign: 'right' }}>{t('செலுத்தியது', 'Paid')}</th>
              <th style={{ textAlign: 'right' }}>{t('எதிர்பார்ப்பு', 'Expected')}</th>
              <th>{t('அடுத்த வழங்கல்', 'Next payout')}</th>
            </tr>
          </thead>
          <tbody>
            {chits.map((c) => (
              <tr key={c.chit.id} onClick={() => nav(`/subscribers/${subscriber_id}/chits/${c.chit.id}`)} style={{ cursor: 'pointer' }}>
                <td>
                  <span className="chit-pill">{lang === 'ta' ? c.chit.name_ta : c.chit.name_en}</span>
                </td>
                <td style={{ textAlign: 'right' }} className="mono">#{c.slot}</td>
                <td style={{ textAlign: 'right' }} className="amount">{format_inr(c.total_paid_paise)}</td>
                <td style={{ textAlign: 'right' }} className="amount">{format_inr(c.total_expected_paise)}</td>
                <td className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                  {c.upcoming_payout_month ?? '—'}
                </td>
              </tr>
            ))}
            {chits.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--ink-3)', padding: 18 }}>
                {t('சீட்டுகளில் இல்லை', 'Not in any chit')}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Payment history */}
      <div className="section-band">{t('செலுத்தல் வரலாறு', 'Payment History')}</div>
      <div className="card" style={{ padding: 0 }}>
        <table className="filings-table">
          <thead>
            <tr>
              <th>{t('தேதி', 'Date')}</th>
              <th>{t('சீட்டு', 'Chit')}</th>
              <th style={{ textAlign: 'right' }}>{t('தொகை', 'Amount')}</th>
              <th>{t('வழி', 'Mode')}</th>
              <th>{t('குறிப்பு', 'Reference')}</th>
            </tr>
          </thead>
          <tbody>
            {payments.map((p) => (
              <tr key={p.payment_id}>
                <td className="mono">{new Date(p.timestamp).toLocaleDateString('en-IN')}</td>
                <td><span className="chit-pill">{lang === 'ta' ? p.chit_name_ta : p.chit_name_en}</span></td>
                <td style={{ textAlign: 'right' }} className="amount">{format_inr(p.amount_paise)}</td>
                <td><span className="mode-tag">{p.method}</span></td>
                <td className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>{p.reference ?? '—'}</td>
              </tr>
            ))}
            {payments.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--ink-3)', padding: 18 }}>
                {t('இன்னும் செலுத்தல் இல்லை', 'No payments yet')}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
